/**
 * Frame for the menu screens: a header with a Back button and the screen
 * title, then a scrolling body. The title takes focus when the screen opens,
 * so screen-reader users hear where they are.
 */
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { IconButton } from './Button.tsx';
import './ui.css';

export interface ScreenLayoutProps {
  title: string;
  onBack?: () => void;
  backLabel?: string;
  /** Extra header content on the far side (e.g. a status badge). */
  headerExtra?: ReactNode;
  /** Modifier class for screen-specific layout. */
  className?: string;
  children: ReactNode;
}

export function ScreenLayout({ title, onBack, backLabel, headerExtra, className, children }: ScreenLayoutProps) {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <main className={['ui-screen', className].filter(Boolean).join(' ')} aria-labelledby={headingId}>
      <header className="ui-screen__header">
        {onBack && backLabel && <IconButton icon="back" label={backLabel} onClick={onBack} className="ui-screen__back" />}
        <h1 id={headingId} ref={headingRef} tabIndex={-1} className="ui-screen__title">
          {title}
        </h1>
        {headerExtra && <div className="ui-screen__extra">{headerExtra}</div>}
      </header>
      <div className="ui-screen__body">{children}</div>
    </main>
  );
}

export interface SectionProps {
  title: string;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** A glassy panel with a heading, for grouping settings and results. */
export function Section({ title, icon, className, children }: SectionProps) {
  const id = useId();
  return (
    <section className={['ui-panel', 'ui-section', className].filter(Boolean).join(' ')} aria-labelledby={id}>
      <h2 id={id} className="ui-section__title">
        {icon}
        <span>{title}</span>
      </h2>
      {children}
    </section>
  );
}
